import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Bar,
  Line,
  ComposedChart,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { formatCurrency, cn } from '../lib/utils';
import { formatCompact, formatPercent, PROPERTY_COLOR_PALETTE } from '../lib/chartFormatters';
import { reportsAPI } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';
import { MonthlyHeatmap } from '../components/MonthlyHeatmap';
import { MoMGrowthRow } from '../components/bros/MoMGrowthRow';
import { generateMonthAxis, monthKeyToLabel, ForecastCaveat } from '../lib/forecastChartUtils';
import { ForecastTotalsFooter } from '../components/dashboard/ForecastTotalsFooter';
import { NotInvoicedCard } from '../components/bros/NotInvoicedCard';

// ── Month index helpers ──────────────────────────────────────────────
// Heatmap always shows Jan-Dec of the current year (matches Portfolio).
// For last_12m / all, data from prior years appears in the YTD/Total
// column but not in individual month cells.
// Reuses the shared generateMonthAxis('ytd') spine (single source of truth).

const MONTH_KEYS = generateMonthAxis('ytd');

function monthIndexToKey(mi) {
  return MONTH_KEYS[mi];
}

// ── KPI Card ─────────────────────────────────────────────────────────

function KpiCard(props) {
  const { label, value, subDetail } = props;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold font-mono">{value}</div>
        {subDetail && (
          <p className="text-xs text-muted-foreground mt-1">{subDetail}</p>
        )}
      </CardContent>
    </Card>
  );
}

// Turnover tile sub-line: booking count, and how many of them are
// cancellations (their fees are in turnover since Fan, 2026-10-24).
export function turnoverSubDetail(kpis, cx, dc) {
  const n = kpis?.total_bookings || 0;
  const c = kpis?.cancellation_bookings || 0;
  if (!c) return `${n} bookings`;
  const fees = formatCurrency(cx(parseFloat(kpis.cancellation_fees || 0)), dc);
  return `${n} bookings, incl. ${c} cancellation${c === 1 ? '' : 's'} (${fees})`;
}

// ── Warnings banner ──────────────────────────────────────────────────

function WarningsBanner(props) {
  const { warnings } = props;
  if (!warnings) return null;
  const items = [];
  if (warnings.fx_missing_count > 0) {
    items.push(`${warnings.fx_missing_count} booking(s) missing FX rate (amount excluded from GBP totals)`);
  }
  if (warnings.zero_vat_count > 0) {
    items.push(`${warnings.zero_vat_count} booking(s) with zero/missing VAT (turnover may overstate)`);
  }
  if (warnings.high_margin_pct_bookings > 0) {
    items.push(`${warnings.high_margin_pct_bookings} booking(s) with margin > 80% (review for data quality)`);
  }
  if (warnings.malformed_company_count > 0) {
    items.push(`${warnings.malformed_company_count} booking(s) with malformed company field (shown under client name or "(unknown)")`);
  }
  if (warnings.segment_supplier_rate_missing_count > 0) {
    items.push(`${warnings.segment_supplier_rate_missing_count} booking(s) with a stay period that has no supplier rate in BR OS (supplier cost, and therefore margin, is overstated)`);
  }
  if (warnings.supplier_fx_excluded_count > 0) {
    items.push(`${warnings.supplier_fx_excluded_count} booking(s) bought in another currency with no usable FX rate (excluded from margin, not counted at face value)`);
  }
  if (warnings.created_at_missing_count > 0) {
    items.push(`${warnings.created_at_missing_count} booking(s) attributed by check-in date due to missing creation date`);
  }
  if (items.length === 0) return null;
  return (
    <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700">
      <CardContent className="pt-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">Data Quality Warnings</p>
            <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-0.5">
              {items.map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Heatmap row builder ──────────────────────────────────────────────

function buildHeatmapRows(section, cx) {
  if (!section) return [];
  const dataRows = section.rows || [];
  const other = section.other;
  const unresolved = section.unresolved;
  const allRows = [
    ...dataRows,
    ...(other ? [other] : []),
    ...(unresolved ? [unresolved] : []),
  ];

  return allRows.map((r) => ({
    id: r.name,
    name: r.name,
    _monthly: r.monthly || {},
    _ytd: r.ytd || {},
    _cx: cx,
  }));
}

// ── P&L chart tooltip ────────────────────────────────────────────────

function PnlChartTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      {row.Revenue != null && (
        <p style={{ color: '#3B82F6' }}>Revenue: {formatCurrency(row.Revenue, currency)}</p>
      )}
      {row.Profit != null && (
        <p style={{ color: '#F59E0B' }}>Profit: {formatCurrency(row.Profit, currency)}</p>
      )}
      {row.Margin != null && (
        <p style={{ color: '#10B981' }}>Margin: {formatPercent(row.Margin, 1)}</p>
      )}
      {row.Bookings != null && (
        <p style={{ color: '#06B6D4' }}>Bookings with an invoice due: {row.Bookings}</p>
      )}
      {row.NewBookings != null && (
        <p style={{ color: '#8B5CF6' }}>New bookings: {row.NewBookings}</p>
      )}
      {row.Cancellation ? (
        <p className="text-muted-foreground">incl. cancellation fees: {formatCurrency(row.Cancellation, currency)}</p>
      ) : null}
      {row.Revenue_forecast ? (
        <p className="text-muted-foreground">Forecast revenue: {formatCurrency(row.Revenue_forecast, currency)}</p>
      ) : null}
      {row.MoMGrowth != null && (
        <p className="text-muted-foreground mt-1 border-t pt-1">
          MoM growth: {row.MoMGrowth >= 0 ? '+' : ''}{formatPercent(row.MoMGrowth, 1)}
        </p>
      )}
    </div>
  );
}

// ── P&L Tab ──────────────────────────────────────────────────────────

// Props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
export function BROSDashboardPnlTab(props) {
  const { data, loading, dc, cx, period, refreshKey } = props;
  const [dimension, setDimension] = useState('client');
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  // Fetch heatmap data on mount and when period/dimension changes
  const fetchHeatmaps = useCallback(async (p, dim) => {
    setHeatmapLoading(true);
    try {
      const res = await reportsAPI.brosHeatmaps(p, dim);
      setHeatmapData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHeatmaps(period, dimension);
  }, [period, dimension, refreshKey, fetchHeatmaps]);

  // ── P&L chart data (Revenue, Profit, Margin, MoM growth, Bookings count) ──
  const pnlChartData = useMemo(() => {
    if (!heatmapData?.revenue?.total?.monthly) return [];
    const revMonthly = heatmapData.revenue.total.monthly;
    const marMonthly = heatmapData.margin?.total?.monthly ?? {};
    const bkgMonthly = heatmapData?.bookings?.total?.monthly ?? {};

    const points = MONTH_KEYS.map((mk) => {
      const revenue = revMonthly[mk]?.revenue;
      const profit = marMonthly[mk]?.margin;
      const revVal = revenue != null ? cx(revenue) : null;
      const profVal = profit != null ? cx(profit) : null;
      const margin = revVal != null && revVal !== 0 && profVal != null
        ? (profVal / revVal) * 100
        : null;
      const bookings = bkgMonthly[mk]?.bookings ?? null;
      return {
        month: monthKeyToLabel(mk),
        monthKey: mk,
        Revenue: revVal,
        Profit: profVal,
        Margin: margin,
        Bookings: bookings,
      };
    });

    // MoM growth: month-over-month revenue change percent
    for (let i = 0; i < points.length; i++) {
      if (i === 0 || points[i].Revenue == null || points[i - 1].Revenue == null || points[i - 1].Revenue === 0) {
        points[i].MoMGrowth = null;
      } else {
        points[i].MoMGrowth = ((points[i].Revenue - points[i - 1].Revenue) / points[i - 1].Revenue) * 100;
      }
    }

    // 3-month forward overlay (Fan, 2026-08-30). data.forecast_months comes
    // from /reports/bros/dashboard and is built from CONTRACTED nights of
    // stays already sold (services/bros_forward_revenue.py), not a
    // projection: a booking running to February contributes its September
    // nights to September. Kept on separate *_forecast keys so a forecast
    // value is never mistaken for -- or summed into -- an actual, and never
    // written onto a month that already has an actual.
    // Cancellation fees are inside Revenue (Fan, 2026-10-24); the tooltip
    // says how much of each month they are.
    for (const m of (data?.months || [])) {
      const fee = parseFloat(m.cancellation_fees || 0);
      const pt = points.find((p) => p.monthKey === m.month);
      if (pt && fee) pt.Cancellation = cx(fee);
      // New bookings = deals closed that month (Fan, 2026-10-27), next to
      // the invoice-due count.
      if (pt && m.new_booking_count != null) pt.NewBookings = m.new_booking_count;
    }

    const forecastByMonth = {};
    for (const fm of (data?.forecast_months || [])) {
      if (fm && fm.month) forecastByMonth[fm.month] = fm;
    }
    for (const point of points) {
      const fc = forecastByMonth[point.monthKey];
      // Stacks on the current month's actual too: the forecast is only the
      // uninvoiced share of live stays plus invoices due later, so it never
      // repeats the actual bar (2026-10-06).
      if (!fc) continue;
      point.Revenue_forecast = cx(parseFloat(fc.turnover || 0));
      point.Profit_forecast = cx(parseFloat(fc.gross_margin || 0));
      point._isForecast = true;
    }

    return points;
  }, [heatmapData, cx, data]);

  const pnlChartHasData = useMemo(
    () => pnlChartData.some((d) => d.Revenue != null || d.Profit != null
      || d.Revenue_forecast != null),
    [pnlChartData]
  );

  const pnlHasForecast = useMemo(
    () => pnlChartData.some((d) => d._isForecast),
    [pnlChartData]
  );

  const chartBookingsTotal = useMemo(
    () => pnlChartData.reduce((sum, d) => sum + (d.Bookings || 0), 0),
    [pnlChartData]
  );

  const maxBookings = useMemo(
    () => Math.max(...pnlChartData.map((d) => Math.max(d.Bookings || 0, d.NewBookings || 0)), 1),
    [pnlChartData]
  );

  // Full 12-month spine: all months Jan-Dec render on the axis; months
  // without data show as empty bars rather than collapsing the axis.
  const growthRowData = useMemo(
    () => pnlChartData.map((d) => ({ monthKey: d.monthKey, MoMGrowth: d.MoMGrowth })),
    [pnlChartData]
  );

  // Value accessors for heatmap rows
  const revenueAccessor = useCallback((row, mi) => {
    const mk = monthIndexToKey(mi);
    const val = row._monthly[mk]?.revenue;
    return val != null && val !== 0 ? row._cx(val) : null;
  }, []);

  const marginAccessor = useCallback((row, mi) => {
    const mk = monthIndexToKey(mi);
    const val = row._monthly[mk]?.margin;
    return val != null ? row._cx(val) : null;
  }, []);

  const bookingsAccessor = useCallback((row, mi) => {
    const mk = monthIndexToKey(mi);
    const val = row._monthly[mk]?.bookings;
    return val != null && val !== 0 ? val : null;
  }, []);

  // YTD accessors
  const revenueYtdAccessor = useCallback((row) => {
    const v = row._ytd?.revenue;
    return v != null ? row._cx(v) : null;
  }, []);

  const marginYtdAccessor = useCallback((row) => {
    const v = row._ytd?.margin;
    return v != null ? row._cx(v) : null;
  }, []);

  const bookingsYtdAccessor = useCallback((row) => {
    const v = row._ytd?.bookings;
    return v != null && v !== 0 ? v : null;
  }, []);

  // Total row accessors (from backend `total` object)
  const revenueTotalAccessor = useCallback((mi) => {
    const mk = monthIndexToKey(mi);
    const v = heatmapData?.revenue?.total?.monthly?.[mk]?.revenue;
    return v != null && v !== 0 ? cx(v) : null;
  }, [heatmapData, cx]);

  const marginTotalAccessor = useCallback((mi) => {
    const mk = monthIndexToKey(mi);
    const v = heatmapData?.margin?.total?.monthly?.[mk]?.margin;
    return v != null ? cx(v) : null;
  }, [heatmapData, cx]);

  const bookingsTotalAccessor = useCallback((mi) => {
    const mk = monthIndexToKey(mi);
    const v = heatmapData?.bookings?.total?.monthly?.[mk]?.bookings;
    return v != null && v !== 0 ? v : null;
  }, [heatmapData]);

  // Build row arrays
  const revenueRows = useMemo(
    () => buildHeatmapRows(heatmapData?.revenue, cx),
    [heatmapData, cx]
  );
  const marginRows = useMemo(
    () => buildHeatmapRows(heatmapData?.margin, cx),
    [heatmapData, cx]
  );
  const bookingsRows = useMemo(
    () => buildHeatmapRows(heatmapData?.bookings, cx),
    [heatmapData, cx]
  );

  // YTD totals from backend
  const revenueYtdTotal = useMemo(() => {
    const v = heatmapData?.revenue?.total?.ytd?.revenue;
    return v != null ? cx(v) : null;
  }, [heatmapData, cx]);

  const marginYtdTotal = useMemo(() => {
    const v = heatmapData?.margin?.total?.ytd?.margin;
    return v != null ? cx(v) : null;
  }, [heatmapData, cx]);

  const marginYtdTotalPct = heatmapData?.margin?.total?.ytd?.margin_pct;

  const marginYtdTotalRenderer = useMemo(() => {
    if (marginYtdTotal == null) return undefined;
    return (
      <span>
        {formatCompact(marginYtdTotal, dc)}
        {marginYtdTotalPct != null && (
          <span className="text-[10px] opacity-70 ml-0.5">({marginYtdTotalPct.toFixed(0)}%)</span>
        )}
      </span>
    );
  }, [marginYtdTotal, marginYtdTotalPct, dc]);

  const bookingsYtdTotal = useMemo(() => {
    const v = heatmapData?.bookings?.total?.ytd?.bookings;
    return v != null && v !== 0 ? v : null;
  }, [heatmapData]);

  // ── Average booking value per month (per-client lines + overall) ──
  // Merge rows + other + unresolved from revenue and bookings sections so
  // clients outside one metric's top-N are not silently dropped.
  const MAX_AVG_BV_CLIENTS = 8;
  const avgBookingValueData = useMemo(() => {
    const revSection = heatmapData?.revenue;
    const bkgSection = heatmapData?.bookings;
    const revTotal = revSection?.total?.monthly;
    const bkgTotal = bkgSection?.total?.monthly;
    if (!revSection?.rows || !bkgSection?.rows || !revTotal || !bkgTotal) {
      return { chartData: [], clientKeys: [], colorMap: {}, capped: false };
    }

    const allRevRows = [
      ...revSection.rows,
      ...(revSection.other ? [revSection.other] : []),
      ...(revSection.unresolved ? [revSection.unresolved] : []),
    ];
    const allBkgRows = [
      ...bkgSection.rows,
      ...(bkgSection.other ? [bkgSection.other] : []),
      ...(bkgSection.unresolved ? [bkgSection.unresolved] : []),
    ];

    // Build lookups by name
    const revByName = {};
    for (const row of allRevRows) revByName[row.name] = row;
    const bkgByName = {};
    for (const row of allBkgRows) bkgByName[row.name] = row;

    // Collect clients that appear in BOTH revenue and bookings
    const clientStats = [];
    const seen = new Set();
    for (const row of [...allRevRows, ...allBkgRows]) {
      const name = row.name;
      if (seen.has(name)) continue;
      seen.add(name);
      const revRow = revByName[name];
      const bkgRow = bkgByName[name];
      if (!revRow || !bkgRow) continue;
      const totalBkgs = bkgRow.ytd?.bookings || 0;
      if (totalBkgs === 0) continue;
      clientStats.push({ name, revRow, bkgRow, totalBkgs });
    }

    // Rank by total booking volume, cap to top N
    clientStats.sort((a, b) => b.totalBkgs - a.totalBkgs);
    const capped = clientStats.length > MAX_AVG_BV_CLIENTS;
    const topClients = clientStats.slice(0, MAX_AVG_BV_CLIENTS);
    const otherClients = capped ? clientStats.slice(MAX_AVG_BV_CLIENTS) : [];

    // Stable colour map: alphabetically sorted names get fixed palette indices
    const colorMap = {};
    const sortedNames = topClients.map((c) => c.name).sort();
    sortedNames.forEach((name, i) => {
      colorMap[name] = PROPERTY_COLOR_PALETTE[i % PROPERTY_COLOR_PALETTE.length];
    });
    if (capped) colorMap['Other'] = '#9CA3AF';

    // Build chart data: one row per month
    const chartData = MONTH_KEYS.map((mk) => {
      const overallRev = revTotal[mk]?.revenue;
      const overallBkg = bkgTotal[mk]?.bookings;
      const overallAvg = (overallRev != null && overallBkg > 0)
        ? Number(cx(parseFloat(overallRev) / overallBkg))
        : null;

      const row = {
        month: monthKeyToLabel(mk),
        'Overall Avg': overallAvg,
      };

      // Per-client averages (top N)
      for (const cs of topClients) {
        const rev = cs.revRow.monthly?.[mk]?.revenue;
        const bkg = cs.bkgRow.monthly?.[mk]?.bookings;
        row[cs.name] = (rev != null && bkg > 0) ? Number(cx(parseFloat(rev) / bkg)) : null;
      }

      // "Other" bucket
      if (capped) {
        let oRev = 0;
        let oBkg = 0;
        for (const cs of otherClients) {
          const rev = cs.revRow.monthly?.[mk]?.revenue;
          const bkg = cs.bkgRow.monthly?.[mk]?.bookings;
          if (rev != null && bkg > 0) {
            oRev += parseFloat(rev);
            oBkg += bkg;
          }
        }
        row['Other'] = oBkg > 0 ? Number(cx(oRev / oBkg)) : null;
      }

      return row;
    });

    const clientKeys = capped
      ? [...topClients.map((c) => c.name), 'Other']
      : topClients.map((c) => c.name);

    return { chartData, clientKeys, colorMap, capped };
  }, [heatmapData, cx]);

  const avgBvHasData = useMemo(
    () => avgBookingValueData.chartData.some((d) => d['Overall Avg'] != null),
    [avgBookingValueData]
  );

  // Last non-null data-point index per client key (for end-of-line labels)
  const avgBvLastIndices = useMemo(() => {
    const map = {};
    const { chartData, clientKeys } = avgBookingValueData;
    for (const key of clientKeys) {
      let lastIdx = -1;
      for (let i = 0; i < chartData.length; i++) {
        if (chartData[i][key] != null) lastIdx = i;
      }
      map[key] = lastIdx;
    }
    return map;
  }, [avgBookingValueData]);

  // YTD label: match Portfolio convention
  const ytdLabel = period === 'ytd' ? 'YTD' : 'Total';
  const rowHeader = dimension === 'country' ? 'Country' : 'Client';

  // Formatters
  const currencyFormatter = useCallback((v) => formatCompact(v, dc), [dc]);
  const countFormatter = useCallback((v) => String(v), []);

  // Margin YTD cell: "£42k (34%)"
  const marginYtdCellRenderer = useCallback((value, row) => {
    const pct = row._ytd?.margin_pct;
    return (
      <span>
        {formatCompact(value, dc)}
        {pct != null && (
          <span className="text-[10px] opacity-70 ml-0.5">({pct.toFixed(0)}%)</span>
        )}
      </span>
    );
  }, [dc]);

  if (!data && !loading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No data available.</p>;
  }

  const kpis = data?.kpis || {};
  const currencyMix = data?.currency_mix || [];

  return (
    <div className="space-y-6">
      {/* ── KPI tiles ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label={`Turnover (${dc})`}
          value={formatCurrency(cx(kpis.total_turnover || 0), dc)}
          subDetail={turnoverSubDetail(kpis, cx, dc)}
        />
        <KpiCard
          label={`COGS (${dc})`}
          value={formatCurrency(cx(kpis.total_cogs || 0), dc)}
          subDetail={`Avg ${formatCurrency(cx(kpis.avg_booking_value || 0), dc)} / booking`}
        />
        <KpiCard
          label={`Gross Margin (${dc})`}
          value={formatCurrency(cx(kpis.total_gross_margin || 0), dc)}
        />
        <KpiCard
          label="Margin %"
          value={formatPercent(kpis.margin_pct || 0, 1)}
          subDetail="Gross margin / turnover"
        />
      </div>

      {/* ── P&L chart (Revenue, Profit, Margin, MoM growth) ── */}
      {pnlChartHasData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue, Profit & Margin</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Revenue = client invoices ex VAT, dated by invoice <strong>due date</strong> (not booking date, not stay date), the same as the Cashflow Invoiced bar. Cancellation fees are included and count as a booking. Profit = revenue less supplier cost. Bookings (invoice due) = bookings with a client invoice due that month, so a long stay billed monthly counts every month. New bookings = bookings closed that month (same rule as the Sales tab), one per room.
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="pnl-forecast-basis">
              Forecast (dashed, this month and the next 2): invoices already raised that fall due in that month, plus the nights of running stays still to come that no invoice covers yet. For this month that means only the remaining nights from tomorrow to month end. Contracted business only, no new sales assumed. Sales by closing date: Sales tab.
            </p>
            <ForecastCaveat show={pnlHasForecast} />
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart data={pnlChartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                {/* Left axis: EUR */}
                <YAxis
                  yAxisId="money"
                  width={50}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(v) => formatCompact(v, dc)}
                />
                {/* Right axis 1: Margin % */}
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  width={40}
                  stroke="#10B981"
                  fontSize={12}
                  tickFormatter={(v) => `${v}%`}
                  domain={[dataMin => Math.min(0, dataMin), 'auto']}
                  allowDataOverflow={false}
                />
                {/* Right axis 2: Booking count */}
                <YAxis
                  yAxisId="bookings"
                  orientation="right"
                  width={35}
                  stroke="#06B6D4"
                  fontSize={12}
                  tickFormatter={(v) => String(Math.round(v))}
                  domain={[0, Math.ceil(maxBookings * 1.3)]}
                />
                <Tooltip content={<PnlChartTooltip currency={dc} />} />
                <Legend />
                <Bar dataKey="Revenue" yAxisId="money" fill="#3B82F6" barSize={28} stackId="rev">
                  <LabelList
                    dataKey="Revenue"
                    position="top"
                    content={({ x, y, width, value, index }) => {
                      if (value == null || value === 0) return null;
                      // A forecast stacked on top carries the combined label.
                      if (pnlChartData[index]?.Revenue_forecast) return null;
                      return (
                        <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fontWeight={600} fill="#3B82F6">
                          {formatCompact(value, dc)}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="Revenue_forecast" name="Revenue (forecast)" yAxisId="money" fill="#3B82F6" stackId="rev"
                  fillOpacity={0.35} stroke="#3B82F6" strokeDasharray="4 2" strokeWidth={1} barSize={28}>
                  <LabelList
                    dataKey="Revenue_forecast"
                    position="top"
                    content={({ x, y, width, value, index }) => {
                      if (value == null || value === 0) return null;
                      const actual = pnlChartData[index]?.Revenue;
                      return (
                        <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="#3B82F6">
                          {actual ? `${formatCompact(actual, dc)} + ${formatCompact(value, dc)}` : formatCompact(value, dc)}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="Profit" yAxisId="money" fill="#F59E0B" barSize={28} stackId="profit">
                  <LabelList
                    dataKey="Profit"
                    position="top"
                    content={({ x, y, width, value }) => {
                      if (value == null || value === 0) return null;
                      return (
                        <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fontWeight={600} fill="#F59E0B">
                          {formatCompact(value, dc)}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="Profit_forecast" name="Profit (forecast)" yAxisId="money" fill="#F59E0B" stackId="profit"
                  fillOpacity={0.35} stroke="#F59E0B" strokeDasharray="4 2" strokeWidth={1} barSize={28} />
                <Line
                  dataKey="Bookings"
                  name="Bookings (invoice due)"
                  yAxisId="bookings"
                  type="monotone"
                  stroke="#06B6D4"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#06B6D4', stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                >
                  <LabelList
                    dataKey="Bookings"
                    position="top"
                    content={({ x, y, value }) => {
                      if (value == null || value === 0) return null;
                      return (
                        <text x={x} y={y - 10} textAnchor="middle" fontSize={10} fontWeight={600} fill="#06B6D4">
                          {value}
                        </text>
                      );
                    }}
                  />
                </Line>
                <Line
                  dataKey="NewBookings"
                  name="New bookings"
                  yAxisId="bookings"
                  type="monotone"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#8B5CF6', stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                >
                  <LabelList
                    dataKey="NewBookings"
                    position="bottom"
                    content={({ x, y, value }) => {
                      if (value == null || value === 0) return null;
                      return (
                        <text x={x} y={y + 16} textAnchor="middle" fontSize={10} fontWeight={600} fill="#8B5CF6">
                          {value}
                        </text>
                      );
                    }}
                  />
                </Line>
                <Line
                  dataKey="Margin"
                  yAxisId="pct"
                  type="monotone"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                >
                  <LabelList
                    dataKey="Margin"
                    position="top"
                    content={({ x, y, value }) => {
                      if (value == null) return null;
                      return (
                        <text x={x} y={y - 10} textAnchor="middle" fontSize={10} fontWeight={600} fill="#10B981">
                          {formatPercent(value, 0)}
                        </text>
                      );
                    }}
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
              <ForecastTotalsFooter
                data={pnlChartData}
                series={[
                  { key: 'Revenue', label: 'Revenue', color: '#3B82F6' },
                  { key: 'Profit', label: 'Gross profit', color: '#10B981' },
                ]}
                currency={dc}
                label="BR OS"
                showCombined={false}
              />
            {/* Align MoM cells under chart month columns (right padding matches right axes + margin) */}
            <div style={{ paddingRight: 85 }}>
              <MoMGrowthRow growthRowData={growthRowData} growthLabel="MoM" />
            </div>
            {chartBookingsTotal > 0 && (
              <p className="text-xs text-muted-foreground text-right mt-2" data-testid="pnl-chart-bookings-total">
                Total: {kpis?.total_bookings ?? chartBookingsTotal} bookings
                {kpis?.total_bookings != null && chartBookingsTotal !== kpis.total_bookings && (
                  <> ({chartBookingsTotal} across months: a stay invoiced over several months counts in each)</>
                )}
                {kpis?.new_bookings != null && (
                  <>. New bookings: {kpis.new_bookings}</>
                )}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Dimension toggle (Client / Country) ── */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Group by:</span>
        <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
          {[
            { value: 'client', label: 'Client' },
            { value: 'country', label: 'Country' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDimension(opt.value)}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded transition-all',
                dimension === opt.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {heatmapLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* ── Revenue heatmap ── */}
      {revenueRows.length > 0 ? (
        <MonthlyHeatmap
          rows={revenueRows}
          rowHeader={rowHeader}
          valueAccessor={revenueAccessor}
          colorScale="blue"
          formatter={currencyFormatter}
          title={`Revenue (${dc})`}
          monthTotalAccessor={revenueTotalAccessor}
          monthTotalFormatter={currencyFormatter}
          ytdAccessor={revenueYtdAccessor}
          ytdLabel={ytdLabel}
          ytdFormatter={currencyFormatter}
          ytdTotal={revenueYtdTotal}
        />
      ) : !heatmapLoading ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No revenue data.</CardContent></Card>
      ) : null}

      {/* ── Margin heatmap ── */}
      {marginRows.length > 0 ? (
        <MonthlyHeatmap
          rows={marginRows}
          rowHeader={rowHeader}
          valueAccessor={marginAccessor}
          colorScale="amber"
          formatter={currencyFormatter}
          title={`Margin (${dc})`}
          monthTotalAccessor={marginTotalAccessor}
          monthTotalFormatter={currencyFormatter}
          ytdAccessor={marginYtdAccessor}
          ytdLabel={ytdLabel}
          ytdFormatter={(v) => formatCompact(v, dc)}
          ytdCellRenderer={marginYtdCellRenderer}
          ytdTotal={marginYtdTotal}
          ytdTotalRenderer={marginYtdTotalRenderer}
        />
      ) : !heatmapLoading ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No margin data.</CardContent></Card>
      ) : null}

      {/* ── Bookings heatmap ── */}
      {bookingsRows.length > 0 ? (
        <MonthlyHeatmap
          rows={bookingsRows}
          rowHeader={rowHeader}
          valueAccessor={bookingsAccessor}
          colorScale="blue"
          formatter={countFormatter}
          title="Bookings"
          monthTotalAccessor={bookingsTotalAccessor}
          monthTotalFormatter={countFormatter}
          ytdAccessor={bookingsYtdAccessor}
          ytdLabel={ytdLabel}
          ytdFormatter={countFormatter}
          ytdTotal={bookingsYtdTotal}
        />
      ) : !heatmapLoading ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No bookings data.</CardContent></Card>
      ) : null}

      {/* ── Average Booking Value chart (client dimension only) ── */}
      {avgBvHasData && dimension === 'client' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Average Booking Value ({dc})</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Turnover divided by booking count per month, per client.
              {avgBookingValueData.capped
                ? ` Top ${MAX_AVG_BV_CLIENTS} clients by volume shown; remainder in "Other".`
                : ''}
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={avgBookingValueData.chartData} margin={{ top: 20, right: 110, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(v) => formatCompact(v, dc)}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const sorted = [...payload]
                      .filter((e) => e.value != null)
                      .sort((a, b) => (b.value || 0) - (a.value || 0));
                    return (
                      <div className="rounded-lg border bg-card p-3 shadow-md text-sm min-w-[200px]">
                        <p className="font-medium mb-1">{label}</p>
                        {sorted.map((entry) => (
                          <div key={entry.name} className="flex items-center justify-between gap-4">
                            <span className="flex items-center gap-1.5">
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span style={{ color: entry.color }}>{entry.name}</span>
                            </span>
                            <span className="font-mono">{formatCurrency(Math.round(entry.value), dc)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="Overall Avg"
                  stroke="#000000"
                  strokeWidth={3}
                  dot={{ r: 3, fill: '#000000' }}
                  connectNulls
                >
                  <LabelList
                    dataKey="Overall Avg"
                    position="top"
                    content={({ x, y, value }) => {
                      if (value == null) return null;
                      return (
                        <text x={x} y={y - 10} textAnchor="middle" fontSize={10} fontWeight={600} fill="#1f2937">
                          {formatCompact(Number(value), dc)}
                        </text>
                      );
                    }}
                  />
                </Line>
                {avgBookingValueData.clientKeys.map((name) => {
                  const lineColor = avgBookingValueData.colorMap[name] || '#6B7280';
                  const lastIdx = avgBvLastIndices[name];
                  return (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={lineColor}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls
                    >
                      <LabelList
                        dataKey={name}
                        content={({ x, y, value, index }) => {
                          if (index !== lastIdx || value == null) return null;
                          return (
                            <text
                              x={x + 6}
                              y={y}
                              textAnchor="start"
                              dominantBaseline="central"
                              fontSize={10}
                              fill={lineColor}
                              fontWeight={500}
                            >
                              {name}
                            </text>
                          );
                        }}
                      />
                    </Line>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ── Currency mix ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Currency Mix</CardTitle>
        </CardHeader>
        <CardContent>
          {currencyMix.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">Bookings</TableHead>
                    <TableHead className="text-right">Turnover (Local)</TableHead>
                    <TableHead className="text-right">Turnover (GBP)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currencyMix.map((row) => (
                    <TableRow key={row.currency}>
                      <TableCell>
                        <Badge variant="outline">{row.currency}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{row.count}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(row.turnover_local, row.currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(row.turnover_gbp, 'GBP')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No currency data available.</p>
          )}
        </CardContent>
      </Card>

      {/* ── Bookings not invoiced: not revenue, kept visible (Fan, 2026-10-24: under Currency Mix) ── */}
      <NotInvoicedCard notInvoiced={data?.not_invoiced} cx={cx} dc={dc} />

      {/* ── Warnings ── */}
      <WarningsBanner warnings={data?.warnings} />

    </div>
  );
}
